import { create } from 'zustand';

/**
 * Sound and haptics preferences. Persisted per device in localStorage; the
 * AudioProvider pushes them into the engine whenever they change.
 */
export interface AudioSettings {
  musicVolume: number;
  effectsVolume: number;
  metronomeVolume: number;
  muted: boolean;
  haptics: boolean;
  /** Screen-reader mode: audio prompts also get a "describe" button. */
  describeAudio: boolean;
  /** How notes are named in prompts and answers. */
  noteNaming: 'english' | 'solfege-fixed' | 'german';
  set: (patch: Partial<Omit<AudioSettings, 'set'>>) => void;
}

const KEY = 'audigoal.audio';

function readStored(): Partial<Omit<AudioSettings, 'set'>> {
  try {
    const raw = localStorage.getItem(KEY);
    if (raw === null) return {};
    const parsed = JSON.parse(raw) as Partial<Omit<AudioSettings, 'set'>>;
    return typeof parsed === 'object' && parsed !== null ? parsed : {};
  } catch {
    return {};
  }
}

function write(state: Omit<AudioSettings, 'set'>): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(state));
  } catch {
    // Private mode or quota: settings simply do not persist.
  }
}

const defaults: Omit<AudioSettings, 'set'> = {
  musicVolume: 1,
  effectsVolume: 0.6,
  metronomeVolume: 0.8,
  muted: false,
  haptics: true,
  describeAudio: false,
  noteNaming: 'english',
};

export const useAudioSettings = create<AudioSettings>((set, get) => ({
  ...defaults,
  ...readStored(),
  set: (patch) => {
    set(patch);
    const { set: _omit, ...rest } = get();
    write(rest);
  },
}));
